import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { getPublicPharmacies, type Pharmacie } from "../../api/publicBenin";
import { sendOrdonnanceToPharmacie } from "../../api/professional";
import { extractOrdonnanceEntityId } from "./ordonnancePharmacyHelpers";

export type OrdonnanceSendOption = { id: string; label: string };

/** Construit les libellés pour le sélecteur à partir des lignes renvoyées par GET /patient/ordonnances/patient/:nip */
export function buildOrdonnanceSendOptionsFromProRows(rows: unknown[]): OrdonnanceSendOption[] {
  const out: OrdonnanceSendOption[] = [];
  for (const row of rows) {
    const o = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
    const id = extractOrdonnanceEntityId(o);
    if (!id) continue;
    const dateRaw = o.datePrescription ?? o.date;
    const dateStr = typeof dateRaw === "string" ? dateRaw : "";
    let dateLabel = "";
    if (dateStr) {
      const d = new Date(dateStr);
      dateLabel = Number.isNaN(d.getTime()) ? dateStr : d.toLocaleDateString("fr-FR");
    }
    const text = String(o.text ?? o.instructions ?? "").trim().slice(0, 72);
    const label = [dateLabel, text || "Ordonnance"].filter(Boolean).join(" — ");
    out.push({ id, label });
  }
  return out;
}

type SendOrdonnanceToPharmacyDialogProps = {
  open: boolean;
  onClose: () => void;
  ordonnanceOptions: OrdonnanceSendOption[];
  onSent?: () => void;
};

export default function SendOrdonnanceToPharmacyDialog({
  open,
  onClose,
  ordonnanceOptions,
  onSent,
}: SendOrdonnanceToPharmacyDialogProps) {
  const [pharmacies, setPharmacies] = useState<Pharmacie[]>([]);
  const [loadingPharmacies, setLoadingPharmacies] = useState(false);
  const [selectedPharmacieId, setSelectedPharmacieId] = useState("");
  const [selectedOrdonnanceId, setSelectedOrdonnanceId] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const optionIdsKey = ordonnanceOptions.map((o) => o.id).join("|");

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSending(false);
    const firstOrdo = ordonnanceOptions[0]?.id ?? "";
    setSelectedOrdonnanceId(firstOrdo);
    setSelectedPharmacieId("");
    setLoadingPharmacies(true);
    void getPublicPharmacies({ limit: 50 })
      .then((res) => {
        if (!res.ok) {
          setError(res.message);
          setPharmacies([]);
          return;
        }
        setPharmacies(res.data);
      })
      .finally(() => setLoadingPharmacies(false));
  }, [open, optionIdsKey, ordonnanceOptions]);

  const handleSubmit = useCallback(async () => {
    const ordonnanceId = selectedOrdonnanceId.trim();
    const pharmacieId = selectedPharmacieId.trim();
    if (!ordonnanceId || !pharmacieId) {
      setError("Sélectionnez une ordonnance et une pharmacie.");
      return;
    }
    setError(null);
    setSending(true);
    const res = await sendOrdonnanceToPharmacie(ordonnanceId, pharmacieId);
    setSending(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    onSent?.();
    onClose();
  }, [onClose, onSent, selectedOrdonnanceId, selectedPharmacieId]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="send-pharma-dialog-title"
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !sending && onClose()} aria-hidden="true" />
      <div
        className="relative w-full max-w-md rounded-[12px] bg-white shadow-xl text-[#0B1F16] max-h-[90vh] flex flex-col"
        style={{ border: "1px solid rgba(0,0,0,0.08)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b shrink-0 flex items-start justify-between gap-3" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
          <h2 id="send-pharma-dialog-title" className="text-lg font-bold pr-2">
            Envoyer l&apos;ordonnance à une pharmacie
          </h2>
          <button
            type="button"
            disabled={sending}
            onClick={onClose}
            className="p-1.5 rounded-[6px] text-[#0B1F16]/70 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40 shrink-0"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-4">
          {ordonnanceOptions.length > 1 && (
            <div>
              <label htmlFor="send-pharma-ordo" className="block text-sm font-medium text-[#0B1F16]/80 mb-2">
                Ordonnance
              </label>
              <select
                id="send-pharma-ordo"
                value={selectedOrdonnanceId}
                onChange={(e) => setSelectedOrdonnanceId(e.target.value)}
                disabled={sending}
                className="w-full h-10 rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                style={{ borderColor: "rgba(0,0,0,0.15)" }}
              >
                {ordonnanceOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="send-pharma-select" className="block text-sm font-medium text-[#0B1F16]/80 mb-2">
              Pharmacie
            </label>
            {loadingPharmacies ? (
              <p className="text-sm text-[#0B1F16]/60">Chargement des pharmacies…</p>
            ) : pharmacies.length === 0 ? (
              <p className="text-sm text-[#0B1F16]/60">Aucune pharmacie disponible.</p>
            ) : (
              <select
                id="send-pharma-select"
                value={selectedPharmacieId}
                onChange={(e) => setSelectedPharmacieId(e.target.value)}
                disabled={sending}
                className="w-full h-10 rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                style={{ borderColor: "rgba(0,0,0,0.15)" }}
              >
                <option value="">Choisir une pharmacie…</option>
                {pharmacies.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nomPharmacie} — {p.commune ?? p.departement ?? "—"}
                  </option>
                ))}
              </select>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-[8px] px-3 py-2">{error}</div>
          )}
        </div>

        <div className="p-5 border-t flex justify-end gap-2 shrink-0" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
          <button
            type="button"
            disabled={sending}
            onClick={onClose}
            className="h-10 px-4 rounded-[6px] font-semibold border text-[#0B1F16] bg-white"
            style={{ borderColor: "rgba(0,0,0,0.2)" }}
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={sending || loadingPharmacies || !selectedPharmacieId || ordonnanceOptions.length === 0}
            onClick={() => void handleSubmit()}
            className="h-10 px-4 rounded-[6px] font-semibold text-white disabled:opacity-50"
            style={{ background: "#00553E" }}
          >
            {sending ? "Envoi…" : "Envoyer"}
          </button>
        </div>
      </div>
    </div>
  );
}
